import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient';
import { ConversationAttachment } from '@provision/entity-types';
import { useEffect } from 'react';
import { filter } from 'rxjs/operators';
import { wsEventBus } from 'src/core/realtime-connection';

export const useChatAttachmentsSubscription = (conversationId?: string) => {
  const apolloClient = useApolloClient();

  const { cache } = apolloClient;

  useEffect(() => {
    if (!conversationId) {
      return;
    }
    const createSubscription = wsEventBus
      .onEntity<ConversationAttachment>({
        name: 'ConversationAttachment',
        operation: 'create',
      })
      .pipe(filter((evt) => evt.attributes.conversationId === conversationId))

      .subscribe({
        next: (event) => {
          const messageId = event.attributes.conversationMessageId;

          const messageToUpdate = cache.identify({
            __typename: 'ConversationMessage',
            id: messageId,
          });

          cache.modify({
            id: messageToUpdate,
            fields: {
              attachments: (existingAttachments = []) => {
                return [
                  ...(existingAttachments as ConversationAttachment[]),
                  event.attributes,
                ];
              },
            },
          });
        },
      });

    const deleteSubscription = wsEventBus
      .onEntity<ConversationAttachment>({
        name: 'ConversationAttachment',
        operation: 'delete',
      })
      .pipe(filter((evt) => evt.previousAttributes.conversationId === conversationId))

      .subscribe({
        next: (event) => {
          const messageId = event.attributes.conversationMessageId;

          const messageToUpdate = cache.identify({
            __typename: 'ConversationMessage',
            id: messageId,
          });

          cache.modify({
            id: messageToUpdate,
            fields: {
              attachments: (existingAttachments = []) => {
                return (existingAttachments as ConversationAttachment[]).filter(
                  (att) => att.id !== event.previousAttributes.id,
                );
              },
            },
          });
        },
      });

    return () => {
      if (!conversationId) {
        return;
      }
      createSubscription.unsubscribe();
      deleteSubscription.unsubscribe();
    };
  }, [cache, conversationId]);
};
